import { useState, useMemo, memo, useCallback, useEffect } from 'react';
import type { LeaderboardEntry, Language } from '@codedrill/shared';
import { fetchLeaderboard } from '../api/sessions';

// ── Types ────────────────────────────────────────────────────────────────────

type Period = 'daily' | 'weekly' | 'alltime';

const ALL_LANGUAGES: Language[] = ['c', 'cpp', 'python'];

// ── Pure helpers ─────────────────────────────────────────────────────────────

function countryFlag(code: string): string {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

function fmtLang(lang: Language): string {
  return lang === 'cpp' ? 'C++' : lang.toUpperCase();
}

const RANK_STYLE: Record<number, { border: string; badge: string; label: string }> = {
  1: { border: '#f0a500', badge: 'lb-badge--gold',   label: '1st' },
  2: { border: '#9ba3ad', badge: 'lb-badge--silver', label: '2nd' },
  3: { border: '#cd6e3a', badge: 'lb-badge--bronze', label: '3rd' },
};

// ── Sub-components ───────────────────────────────────────────────────────────

interface FilterBarProps {
  minAccuracy: number;
  period: Period;
  language: Language | 'all';
  onMinAccuracy: (v: number) => void;
  onPeriod: (v: Period) => void;
  onLanguage: (v: Language | 'all') => void;
}

const FilterBar = memo(function FilterBar({
  minAccuracy, period, language,
  onMinAccuracy, onPeriod, onLanguage,
}: FilterBarProps) {
  return (
    <div className="lb-filters">
      {/* Period */}
      <div className="lang-selector" role="group" aria-label="Time period">
        {(['daily', 'weekly', 'alltime'] as Period[]).map((p) => (
          <button
            key={p}
            className={`lang-btn${period === p ? ' lang-btn--active' : ''}`}
            onClick={() => onPeriod(p)}
          >
            {p === 'alltime' ? 'all time' : p}
          </button>
        ))}
      </div>

      {/* Language */}
      <div className="lang-selector" role="group" aria-label="Language">
        <button
          className={`lang-btn${language === 'all' ? ' lang-btn--active' : ''}`}
          onClick={() => onLanguage('all')}
        >
          all
        </button>
        {ALL_LANGUAGES.map((l) => (
          <button
            key={l}
            className={`lang-btn${language === l ? ' lang-btn--active' : ''}`}
            onClick={() => onLanguage(l)}
          >
            {fmtLang(l)}
          </button>
        ))}
      </div>

      {/* Min accuracy */}
      <label className="lb-acc-filter">
        <span className="lb-acc-label">acc ≥ {minAccuracy}%</span>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={minAccuracy}
          onChange={(e) => onMinAccuracy(Number(e.target.value))}
          className="lb-acc-range"
        />
      </label>
    </div>
  );
});

interface EntryCardProps {
  entry: LeaderboardEntry;
}

const EntryCard = memo(function EntryCard({ entry }: EntryCardProps) {
  const rs = RANK_STYLE[entry.rank];

  return (
    <div
      className={`lb-card${entry.rank <= 3 ? ' lb-card--top' : ''}`}
      style={rs ? { borderLeftColor: rs.border } : undefined}
    >
      {/* ── Row 1: rank badge + username ── */}
      <div className="lb-card-header">
        <span className={`lb-badge${rs ? ` ${rs.badge}` : ''}`}>
          {rs ? rs.label : `#${entry.rank}`}
        </span>
        <div className="lb-user">
          <span className="lb-username">{entry.username}</span>
        </div>
      </div>

      {/* ── Row 2: stats pills ── */}
      <div className="lb-card-stats">
        <span className="lb-stat lb-stat--wpm">{entry.wpm} <em>wpm</em></span>
        <span className="lb-stat">{entry.accuracy.toFixed(1)}% <em>acc</em></span>
        <span className="lb-stat lb-stat--lang">{fmtLang(entry.language as Language)}</span>
      </div>
    </div>
  );
});

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LeaderboardPage() {
  const [period, setPeriod]       = useState<Period>('alltime');
  const [language, setLanguage]   = useState<Language | 'all'>('all');
  const [minAccuracy, setMinAcc]  = useState(0);

  const [entries, setEntries]     = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // Fetch from real API whenever period or language changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchLeaderboard(language === 'all' ? undefined : language, period, 50)
      .then((res) => {
        if (!cancelled) {
          setEntries(res.entries);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load leaderboard');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [period, language]);

  // Client-side accuracy filter (no extra round trip)
  const filtered = useMemo(
    () => entries.filter((e) => e.accuracy >= minAccuracy),
    [entries, minAccuracy],
  );

  const handlePeriod   = useCallback((v: Period) => setPeriod(v), []);
  const handleLanguage = useCallback((v: Language | 'all') => setLanguage(v), []);
  const handleMinAcc   = useCallback((v: number) => setMinAcc(v), []);

  // Collect unique country codes for flag display (real entries may have them in future)
  const flagMap = useMemo(() => {
    const flags: Record<string, string> = {};
    filtered.forEach((e) => {
      // entries don't carry countryCode yet — placeholder for future
      void e;
    });
    return flags;
  }, [filtered]);
  void flagMap;
  void countryFlag;

  return (
    <div className="leaderboard-page">
      <h1 className="page-title">leaderboard</h1>

      <FilterBar
        minAccuracy={minAccuracy}
        period={period}
        language={language}
        onMinAccuracy={handleMinAcc}
        onPeriod={handlePeriod}
        onLanguage={handleLanguage}
      />

      {loading ? (
        <p className="leaderboard-empty lb-empty-msg">loading...</p>
      ) : error ? (
        <p className="leaderboard-empty lb-empty-msg">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="leaderboard-empty lb-empty-msg">no entries yet — be the first!</p>
      ) : (
        <div className="lb-grid">
          {filtered.map((entry) => (
            <EntryCard key={entry.sessionId} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
